<template>
  <div class="keywords-header">
    <div class="keywords-header__header">
      <button class="keywords-header__back" @click="$router.push(paths.tendersInbox)" :aria-label="$t('menu.inbox')">
        <BaseIcon name="arrow-left" />
      </button>
      <div>
        <h1 class="keywords-header__title">
          {{ $t('menu.keywords') }}
          <div class="keywords__explanation">
            <keyword-explanation />
          </div>
        </h1>
        <p v-if="!isPhone" class="keywords-header__count">
          <span class="number-monospace">{{ formatLocalizedNumber(keywordCount) }}</span
          >{{ ' ' + $tc('page.keywords.count', keywordCount) }}
        </p>
      </div>
    </div>

    <div class="keywords-header__right">
      <!-- Phones: under the title, the button with the question that says what it's for (an icon
           alone wouldn't). -->
      <div class="keywords-header__contact">
        <div class="keywords-header__contact-icon">
          <BaseIcon class="keywords-header__icon" name="message-sent" />
        </div>
        <div class="keywords-header__contact-action">
          <div class="keywords-header__contact-text" v-html="$t('page.keywords.request-changes')"></div>
          <BaseButton @click.native="toggleBeacon" is-small>{{ $t('page.keywords.contact') }}</BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseButton from '@/components/base/BaseButton';
import { Beacon } from '@/plugins/beacon';
import KeywordExplanation from '@/components/page-keywords/KeywordExplanation';
import { formatLocalizedNumber } from '@/util/number-util';
import { paths } from '@/util/paths';
import { viewport } from '@/util/viewport';

export default {
  name: 'KeywordHeader',
  components: {
    BaseIcon,
    BaseButton,
    KeywordExplanation,
  },
  props: ['keywordCount'],
  computed: {
    paths() {
      return paths;
    },
    isPhone() {
      return viewport.size === 'phone';
    },
  },
  methods: {
    formatLocalizedNumber,
    toggleBeacon() {
      this.$track.contactUs('keywords');
      Beacon.toggle();
      Beacon.navigate('ask');
    },
  },
};
</script>

<style lang="scss">
.keywords-header {
  background: $color-white;
  box-shadow: $shadow-dropdown;
  display: flex;
  padding: 0px 32px;
  align-items: center;
  height: $header-height;
  position: sticky;
  top: $menu-top-bar-height;
  z-index: $header-index;

  &__right {
    display: flex;
    margin-left: auto;
  }

  &__title {
    color: $color-normal;
    font-family: $font-family-raleway;
    font-size: $font-size-page-title;
    font-weight: normal;
    margin: 0;
    text-transform: uppercase;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    align-items: flex-start;
  }

  &__count {
    color: $color-neutral;
    font-size: $font-size-small;
    font-weight: bold;
    text-transform: uppercase;
  }

  &__icon {
    height: 60px;
    margin-right: 30px;
    width: 60px;
  }

  &__contact {
    height: $header-height;
    padding-left: 24px;
    border-left: 1px solid $color-border;
    display: flex;
    max-width: 450px;

    &-icon {
      height: 60px;
      margin-right: 16px;
      width: 60px;
      align-self: center;
    }

    &-action {
      align-items: center;
      display: flex;
      gap: 8px;
    }

    &-text {
      font-family: $font-family-raleway;
      font-size: $font-size-small;
      width: 50%;
    }
  }

  &__header {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: flex-start;
  }

  // Back arrow → inbox (the keywords page is reached from the inbox header now).
  &__back {
    align-items: center;
    background: none;
    border: none;
    color: $color-normal;
    cursor: pointer;
    display: flex;
    margin: 4px 12px 0 0;
    padding: 4px;
    border-radius: $border-radius-small;
    transition: background 150ms ease, color 150ms ease;

    &:hover {
      background: $color-hover;
      color: $color-accent;
    }

    .feather {
      height: 22px;
      width: 22px;
    }
  }

  @media screen and (max-width: 1080px) {
    &__title {
      font-size: 25px;
    }
  }

  @media screen and (max-width: 650px) {
    &__contact {
      max-width: 160px;

      &-icon {
        display: none;
      }
      &-text {
        font-size: 12px;
      }
    }
  }

  // Phones: back, the title and its help; under them, the question and its button. The count
  // goes to the count line under the languages.
  @include phone-page-header {
    flex-wrap: wrap;
    gap: 0 4px;
    padding: 6px $space-1 0;
  }

  &__right {
    @include screen-below('tablet') {
      flex-basis: 100%;
      margin-left: 0;
    }
  }

  &__contact {
    @include screen-below('tablet') {
      flex: 1;
      height: auto;
      max-width: none;
      padding: $space-2 $space-3 $space-2;
      border-top: 1px solid $color-border;
      border-left: 0;

      &-action {
        flex: 1;
        justify-content: space-between;
        gap: $space-3;
      }

      &-text {
        width: auto;
        font-size: 13px;
      }

      .base-button__element {
        min-height: 44px;
        white-space: nowrap;
      }
    }
  }

  &__header {
    @include screen-below('tablet') {
      flex: 1;
      align-items: center;
      min-width: 0;
    }
  }

  &__back {
    @include screen-below('tablet') {
      justify-content: center;
      width: 44px;
      height: 44px;
      margin: 0;
    }
  }

  &__title {
    @include screen-below('tablet') {
      align-items: center;
      gap: 8px;
      font-size: 20px;
      line-height: 28px;
    }
  }
}
</style>
